import React, { useEffect, useState } from 'react';
import { SplitPanelUtil } from '../../../commonUtils/SplitPanelUtil';
import TreeNode from '../../../commonUtils/BMSPPrimereactFork/treenode/TreeNode';
import { IUseFetch, useFetch } from '../../../commonUtils/useFetch';
import authService from '../../../../authService';
import { IHierarchyBuilderNodeData, IPermission } from '../../onboardingModels';
import { consumeAPIs, IAPIPackage } from '../../../commonUtils/APIContext';
import { Button, Icon, Input, Select, Tab } from '@scuf/common';
import i18next from '../../../../i18next';
import {
  camelToPascalCase,
  editablePropertyInputTypes,
  filterUIProperties,
  getNodePath,
  isNodeRootNode,
  nodeTypes,
  nodeTypesOptions,
  timeZoneIds,
  pascalToCamelCase,
  arrayToOptions,
  FieldElementType
} from '../../onboardingUtils';
import { IOption } from '@scuf/common/dist/components/Select/ISelectProps';
import toasts from '../../../commonUtils/toasts';

const propertiesToShowOnEditOrg = [
  'Name',
  'PrimaryContactName',
  'PrimaryContactEmail',
  'Telephone',
  'Address',
  'TimeZone'
];
const propertiesToShowOnEditSite = [
  'Name',
  'Type',
  'ContactEmail',
  'Telephone',
  'Address',
  'TimeZone'
  // 'CountryCode'
  // 'Latitude',
  // 'Longitude'
];
const propertiesToShowOnEditGeneric = [
  'Name',
  'Type'
  // 'Address'
];

interface IHierarchyBuilderEditSidePanel extends IAPIPackage {
  onClose: () => void;
  onFinish: () => void;
  selectedNode: TreeNode<IHierarchyBuilderNodeData>;
}
export const HierarchyBuilderEditSidePanel = (
  props: IHierarchyBuilderEditSidePanel
) => {
  interface IEditedField {
    name: string;
    value: any;
    element: FieldElementType;
    patturn?: string;
  }

  const [editedFields, setEditedFields] = useState<IEditedField[]>();

  const onEditField = (
    propertyName: string,
    newValue: any,
    element: FieldElementType,
    patturn?: string
  ) => {
    setEditedFields(prev => {
      const updatedField: IEditedField = {
        name: propertyName,
        value: newValue,
        element: element,
        patturn: patturn
      };
      if (prev) {
        const foundIndex = prev.findIndex(field => field.name === propertyName);
        if (foundIndex > -1) {
          prev[foundIndex] = updatedField;
          return [...prev];
        } else {
          return [...prev, updatedField];
        }
      } else {
        return [updatedField];
      }
    });
  };

  const [sendingPatchEdit, setSendingPatchEdit] = useState<boolean>(false);

  const sendEditProperties = () => {
    const body = editedFields?.map(field => {
      return {
        op: 'add',
        path: `${getNodePath(props.selectedNode)}/${camelToPascalCase(
          field.name
        )}`,
        value: field.value
      };
    });

    setSendingPatchEdit(true);

    authService
      .authFetch(
        `${authService.BASE_URL}/api/v1/organisations/LCBS/${props.selectedNode.data?.rootNodeId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        }
      )
      .then(res => {
        if (res.ok) {
          props.onFinish();
        } else {
          if (res.status === 400) {
            toasts.error(i18next.t('saveBadRequestError'));
          }
        }
      })
      .catch(e => {
        toasts.error(i18next.t('saveError'));
      })
      .finally(() => {
        setSendingPatchEdit(false);
      });
  };

  const getEditedProperty = (key: string) => {
    return editedFields?.find(obj => key === obj.name);
  };

  const [selectFocused, setSelectFocused] = useState<string>();

  const getElementForMetadata = (dataKey: string, value: string) => {
    const elementSpec = editablePropertyInputTypes.find(
      obj => dataKey === obj.name
    );
    let inputValid: boolean = true;
    if (elementSpec?.patturn) {
      const regex = new RegExp(elementSpec?.patturn);
      const propertyValue = getEditedProperty(dataKey)?.value;
      inputValid = propertyValue ? regex.test(propertyValue) : true;
    }
    switch (elementSpec?.element) {
      case 'input':
        return (
          <Input
            key={'key' + dataKey + value}
            fluid={true}
            reserveSpace={true}
            label={i18next.t(dataKey)}
            type={elementSpec.type}
            defaultValue={value}
            onChange={input => {
              onEditField(
                elementSpec.name,
                input,
                elementSpec.element,
                elementSpec.patturn
              );
            }}
            error={inputValid ? undefined : elementSpec.patturnError}
          />
        );
      case 'select':
        let options: IOption[] = [];
        switch (elementSpec.type) {
          case 'nodeType':
            options = nodeTypesOptions;
            break;
          case 'timeZones':
            options = arrayToOptions(timeZoneIds);
            break;
          default:
        }

        /**
         * This is to lazy load the select options in when focued, and unload them again when unfocused
         * This is because the render refresh gets slowed down a lot, making other input fields laggy.
         */
        const possibleSelectedOptions = [
          { text: value, value: value },
          ...(editedFields
            ? editedFields
                .filter(field => field.element === elementSpec.element)
                .map(field => {
                  return {
                    text: field.value,
                    value: field.value
                  } as IOption;
                })
            : [])
        ];

        return (
          <Select
            key={'key' + dataKey + value}
            fluid={true}
            reserveSpace={true}
            label={i18next.t(dataKey)}
            options={
              selectFocused === elementSpec.name
                ? options
                : possibleSelectedOptions
            }
            defaultValue={value}
            onFocus={() => setSelectFocused(elementSpec.name)}
            onBlur={() => setSelectFocused(undefined)}
            onChange={value => {
              onEditField(
                elementSpec.name,
                value,
                elementSpec.element,
                elementSpec.patturn
              );
            }}
          />
        );
      default:
    }
    return <Input type="text" />;
  };

  const getPropertiesToEditForNodeType = (node: TreeNode) => {
    switch (node.data.type) {
      case 'Organisation':
        return propertiesToShowOnEditOrg;
      case 'Site':
        return propertiesToShowOnEditSite;
      default:
        return propertiesToShowOnEditGeneric;
    }
  };

  const handleOnClose = () => {
    if (!sendingPatchEdit) {
      props.onClose();
    }
  };

  const saveValid = () => {
    // For the optional patturns, check all are valid. No patturn supplied means valid.
    const allInputsMatchSuppliedPatturn = editedFields?.every(field => {
      if (field.patturn) {
        const regex = new RegExp(field.patturn);
        return regex.test(field.value);
      } else {
        return true;
      }
    });

    // Are they different to current value
    const editedFieldsAreChanged = editedFields?.some(field => {
      const result = props.selectedNode.data
        ? props.selectedNode.data[field.name] !== field.value
        : true;
      return result;
    });

    const isValid = editedFields
      ? allInputsMatchSuppliedPatturn && editedFieldsAreChanged
      : false;
    return isValid;
  };

  return (
    <SplitPanelUtil.Panel onOverlayClicked={handleOnClose}>
      <SplitPanelUtil.PanelHeader onPanelClose={handleOnClose}>
        <SplitPanelUtil.PanelHeaderTitle>
          {props.selectedNode.data?.name}
        </SplitPanelUtil.PanelHeaderTitle>
      </SplitPanelUtil.PanelHeader>
      <SplitPanelUtil.PanelContent>
        <Tab defaultActiveIndex={0} onTabChange={() => {}}>
          <Tab.Pane title={i18next.t('details')}>
            {/* <div className="panel-tab-content"> */}
            <SplitPanelUtil.PanelContentMain>
              <SplitPanelUtil.PanelContentMainSection>
                {getPropertiesToEditForNodeType(props.selectedNode)
                  .map(pascalToCamelCase)
                  .map((dataKey, index) => {
                    const value = props.selectedNode.data
                      ? props.selectedNode.data[dataKey]
                      : '';
                    return (
                      <React.Fragment key={index + dataKey}>
                        {dataKey !== 'nodeId'
                          ? getElementForMetadata(dataKey, value)
                          : null}
                      </React.Fragment>
                    );
                  })}
              </SplitPanelUtil.PanelContentMainSection>
            </SplitPanelUtil.PanelContentMain>
            {/* </div> */}
          </Tab.Pane>
        </Tab>
      </SplitPanelUtil.PanelContent>
      <SplitPanelUtil.PanelFooter>
        <Button
          type={'secondary'}
          onClick={handleOnClose}
          disabled={sendingPatchEdit}
        >
          {i18next.t('cancel')}
        </Button>
        <Button
          disabled={sendingPatchEdit || !saveValid()}
          onClick={sendEditProperties}
          loading={sendingPatchEdit}
        >
          {i18next.t('save')}
        </Button>
      </SplitPanelUtil.PanelFooter>
    </SplitPanelUtil.Panel>
  );
};

export default consumeAPIs(HierarchyBuilderEditSidePanel);
